import { useState, useEffect, useRef } from "react";
import { Camera, CheckCircle, X } from "lucide-react";
import { Dialog } from "../../components/Dialog";
import Button from "../../components/Button";
import TextArea from "../../components/Textarea";
import { api } from "../../helpers/api";

interface SupportTicketModalProps {
  open: boolean;
  onClose: () => void;
  conversationContext?: Array<{ role: string; content: string }>;
  initialMessage?: string;
  source?: string;
}

export function SupportTicketModal({
  open,
  onClose,
  conversationContext,
  initialMessage,
  source = "chatbot",
}: SupportTicketModalProps) {
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const autoDismissTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (open) {
      setMessage(initialMessage ?? "");
      setScreenshot(null);
      setStatus("idle");
      setErrorMessage("");
    }
    return () => clearTimeout(autoDismissTimer.current);
  }, [open, initialMessage]);

  const handleCaptureScreenshot = async () => {
    setIsCapturing(true);
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        preferCurrentTab: true,
        video: {
          displaySurface: "browser",
          width: { ideal: 3840 },
          height: { ideal: 2160 },
        },
        audio: false,
      } as DisplayMediaStreamOptions);

      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      await new Promise<void>((resolve) => {
        if (video.videoWidth > 0) return resolve();
        video.onresize = () => resolve();
      });

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d")!.drawImage(video, 0, 0);
      video.pause();
      video.srcObject = null;
      setScreenshot(canvas.toDataURL("image/jpeg", 1.0));
    } catch (err) {
      if ((err as DOMException).name !== "NotAllowedError") {
        console.error("Screenshot capture failed:", err);
      }
    } finally {
      stream?.getTracks().forEach((t) => t.stop());
      setIsCapturing(false);
    }
  };

  const handleSubmit = async () => {
    if (!message.trim() || status === "submitting") return;
    setStatus("submitting");
    setErrorMessage("");

    try {
      await api.supportTickets.create({
        message: message.trim(),
        screenshot: screenshot ?? undefined,
        conversationContext,
        source,
        page: window.location.pathname,
      });

      setStatus("success");
      autoDismissTimer.current = setTimeout(onClose, 2000);
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong"
      );
    }
  };

  if (!open || isCapturing) return null;

  const isSuccess = status === "success";

  return (
    <Dialog
      variant="center"
      size="sm"
      onClose={onClose}
      title={isSuccess ? undefined : "Contact Support"}
      subtitle={
        isSuccess ? undefined : "Describe your issue and we'll get back to you"
      }
      secondaryAction={
        isSuccess
          ? undefined
          : { label: "Cancel", onClick: onClose, variant: "ghost" }
      }
      primaryAction={
        isSuccess
          ? undefined
          : {
              label: "Send Message",
              onClick: handleSubmit,
              disabled: !message.trim() || status === "submitting",
              loading: status === "submitting",
            }
      }
    >
      {isSuccess ? (
        <div className="flex flex-col items-center gap-3 py-4">
          <CheckCircle className="text-brand-primary" size={40} />
          <p className="text-headline font-semibold text-content-primary">
            Support ticket submitted!
          </p>
          <p className="text-subhead text-content-secondary">
            We'll get back to you soon.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <TextArea
            label=""
            placeholder="Describe your issue..."
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
            rows={8}
          />
          <p className="text-right text-footnote text-content-tertiary -mt-4">
            {message.length}/2000
          </p>

          <div>
            <p className="text-callout font-medium text-content-primary mb-2">
              Screenshot (optional)
            </p>
            {screenshot ? (
              <div className="relative inline-block">
                <img
                  src={screenshot}
                  alt="Screenshot preview"
                  className="h-16 rounded-lg border border-border-primary"
                />
                <button
                  type="button"
                  onClick={() => setScreenshot(null)}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow-md hover:bg-surface-secondary transition-colors"
                  aria-label="Remove screenshot"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCaptureScreenshot}
                disabled={isCapturing}
                loading={isCapturing}
              >
                {!isCapturing && <Camera size={14} />}
                {isCapturing ? "Capturing..." : "Capture Screenshot"}
              </Button>
            )}
          </div>

          {status === "error" && (
            <p className="text-danger text-xs">{errorMessage}</p>
          )}
        </div>
      )}
    </Dialog>
  );
}
